import { useLazyQuery } from '@apollo/client'
import { useUrlSearchParams } from '@dai/common'
import styled from '@emotion/styled'
import { Grid2 as Grid, Typography as MuiTypography } from '@mui/material'
import { spacing } from '@mui/system'
import dayjs from 'dayjs'
import { isEmpty, isNil } from 'lodash'
import { useEffect } from 'react'
import DAIHomeJoyRide from 'tours/DAIHomeJoyRide'

import FeatureWrapper from '@/components/common/FeatureWrapper'
import { DaiDivider } from '@/components/DaiDivider'
import { DataCoverageAlert } from '@/components/DataCoverageAlert'
import StatWithProgress from '@/components/reports/StatWithProgress'
import { DEFAULT_DATA_COVERAGE_THRESHOLD } from '@/constants'
import { useRefresh } from '@/contexts/RefreshContext'
import {
  AggregateDashboardStatsQuery,
  AggregateDashboardStatsQueryVariables,
  DashboardStatsIntervalQuery,
  DashboardStatsIntervalQueryVariables,
  GET_AGGREGATE_DASHBOARD_STATS,
  GET_INTERVAL_DASHBOARD_STATS,
  GroupByDate,
  PlanType,
} from '@/graphql'
import { useIsSingleStore } from '@/hooks/use-is-single-store'
import { usePosDataSetupStatus } from '@/hooks/use-pos-data-setup-status'
import { useDataCoverage } from '@/hooks/useDataCoverage'
import { useSetDefaultTimeRange } from '@/hooks/useSetDefaultTimeRange'
import useT from '@/i18n/useT'
import { ChartHelpers } from '@/utils/chart.helpers'
import { DashboardStatsHelpers } from '@/utils/dashboardStats.helpers'
import { DateHelpers } from '@/utils/date.helpers'
import { computePercentChange, formatAmount } from '@/utils/statCards'

import AIInsightsTable from './AIInsightsTable'
import Stats from './Stats'
import StoresReportTable from './StoresReportTable'

const Typography = styled(MuiTypography)(spacing)

export type HomeDashboardParams = {
  startDate: string
  endDate: string
  storeIds: string[]
  categoryIds: string[]
  actionShiftIds: string[]
  searchTerm?: string
  categories?: string[]
  tab?: string
}

export type ID = {
  id: string
}

export type LabelOpt = {
  label: string
} & ID

export const getStatOnClick = (
  handleNavTo: (path: string, params?: any) => void,
  key: string,
  singleStoreId?: string,
): { onClick: () => void } => {
  if (!singleStoreId) {
    const dict: { [key in string]: () => void } = {
      task: () => handleNavTo('/tasks'),
      restock: () => handleNavTo('/restocks'),
      rev: () => handleNavTo('/home/revenue'),
    }

    return { onClick: dict[key] }
  } else {
    const dictSingle: { [key in string]: () => void } = {
      task: () => handleNavTo(`/stores/${singleStoreId}/tasks`),
      restock: () => handleNavTo(`/stores/${singleStoreId}/restocks`),
      rev: () => handleNavTo(`/stores/${singleStoreId}/revenue`),
    }

    return { onClick: dictSingle[key] }
  }
}
export const getStatUpgradeMessage = (
  t: (key: string) => string,
  key: string,
) => {
  const dict: { [key in string]: string } = {
    task: t('stats.upgradeMessage.task'),
    restock: t('stats.upgradeMessage.restock'),
    rev: t('stats.upgradeMessage.rev'),
    empty: t('stats.upgradeMessage.empty'),
    compliance: t('stats.upgradeMessage.compliance'),
  }
  return dict[key] || null
}

const HomeSummary = () => {
  const { t } = useT()
  const {
    state: urlState,
    handle: { handleUpdateSearchParams, handleNavTo },
  } = useUrlSearchParams<HomeDashboardParams>()
  const { startDate, endDate, storeIds } = urlState
  const startDateWithTime = startDate
    ? DateHelpers.getStartOfDayWithTimezone(startDate)
    : null
  const endDateWithTime = endDate
    ? DateHelpers.getEndOfDayWithTimezone(endDate)
    : null

  const [
    fetchDashboardIntervalStats,
    { data: chartResults, refetch: refetchChart, variables: chartVariables },
  ] = useLazyQuery<
    DashboardStatsIntervalQuery,
    DashboardStatsIntervalQueryVariables
  >(GET_INTERVAL_DASHBOARD_STATS, { notifyOnNetworkStatusChange: true })

  const [
    fetchAggregateStats,
    {
      data: aggregateStats,
      loading: aggregateStatsLoading,
      refetch: refetchAggregateStats,
      variables: aggregateVariables,
    },
  ] = useLazyQuery<
    AggregateDashboardStatsQuery,
    AggregateDashboardStatsQueryVariables
  >(GET_AGGREGATE_DASHBOARD_STATS, { notifyOnNetworkStatusChange: true })

  useSetDefaultTimeRange({
    startDate,
    endDate,
    updateSearchParams: handleUpdateSearchParams,
  })

  useEffect(() => {
    if (startDateWithTime && endDateWithTime) {
      fetchDashboardIntervalStats({
        variables: {
          filters: {
            ...ChartHelpers.getChartWindow(
              startDateWithTime,
              endDateWithTime,
              new Date().toISOString(),
            ),
            storeIds,
          },
        },
      })
      fetchAggregateStats({
        variables: {
          filters: {
            startDate: startDateWithTime,
            endDate: endDateWithTime,
            storeIds,
          },
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    startDateWithTime,
    endDateWithTime,
    fetchDashboardIntervalStats,
    storeIds?.length,
    fetchAggregateStats,
  ])

  const { isSingleStore } = useIsSingleStore(storeIds)

  const handleRefresh = () => {
    if (!isEmpty(chartVariables)) {
      refetchChart()
    }
    if (!isEmpty(aggregateVariables)) {
      refetchAggregateStats()
    }
  }

  useRefresh(handleRefresh)

  const dataGranularity =
    chartResults?.intervalDashboardStats?.period || GroupByDate.DAY
  const endDateWithinLast7Days =
    !endDate || dayjs(endDate).isAfter(dayjs().subtract(7, 'day'), 'day')

  const posDataSetupStatus = usePosDataSetupStatus({ storeIds })

  const dataCoverage = useDataCoverage({
    chartData: chartResults?.intervalDashboardStats?.results,
    startDate,
    endDate,
    dataGranularity,
    threshold: DEFAULT_DATA_COVERAGE_THRESHOLD,
  })

  return (
    <>
      <DAIHomeJoyRide />
      {dataCoverage && (
        <DataCoverageAlert
          dataCoverage={dataCoverage}
          chartData={chartResults?.intervalDashboardStats?.results}
          currentStartDate={startDate}
          currentEndDate={endDate}
          onAdjustDateRange={(suggestedStartDate, suggestedEndDate) => {
            handleUpdateSearchParams({
              startDate: suggestedStartDate,
              endDate: suggestedEndDate,
            })
          }}
        />
      )}
      <Grid container spacing={3} mt={3}>
        {DashboardStatsHelpers.transformStats(
          aggregateStats?.aggregateDashboardStats,
          t,
          startDate,
          endDate,
        ).map(stat => {
          const change =
            isNil(stat.value) ||
            DateHelpers.isSingleDayToday(startDate, endDate)
              ? null
              : computePercentChange(stat)
          const isPercentage = stat.valueType === 'PERCENTAGE'
          const { onClick } = getStatOnClick(handleNavTo, stat.name)

          return (
            <Grid
              key={stat.label}
              size={{ xs: 12, sm: 6, md: 4, lg: 4, xl: 'grow' }}
              sx={{ display: 'flex', flexDirection: 'column' }}
            >
              <FeatureWrapper
                minHeight={0}
                title={stat.label}
                upgradeMessage={getStatUpgradeMessage(t, stat.name)}
                posDataSetupStatus={posDataSetupStatus}
                requiredPlan={stat.requiredPlan}
                needsPosData={stat.name === 'rev'} // TODO: make this a backend field
              >
                <Stats
                  title={stat.label}
                  onClick={onClick}
                  amount={
                    isNil(stat.value) ? (
                      <Typography variant="h6" gutterBottom>
                        {t('homeDashboard.stats.noData')}
                      </Typography>
                    ) : isPercentage ? (
                      <StatWithProgress value={stat.value || 0} />
                    ) : (
                      formatAmount(stat)
                    )
                  }
                  change={change}
                  changeTitle={stat.changeTitle}
                  changeTooltip={stat.changeTooltip}
                  loading={aggregateStatsLoading}
                />
              </FeatureWrapper>
            </Grid>
          )
        })}
      </Grid>
      <DaiDivider my={3} />
      {endDateWithinLast7Days && (
        <>
          <FeatureWrapper
            minHeight={0}
            title={t('aiInsightsTable.title')}
            upgradeMessage={t('aiInsightsTable.upgradeMessage')}
            requiredPlan={PlanType.PREMIUM}
            needsPosData={false}
            posDataSetupStatus={posDataSetupStatus}
          >
            <AIInsightsTable storeIds={storeIds} />
          </FeatureWrapper>
          <DaiDivider />
        </>
      )}
      {!isSingleStore && (
        <>
          <StoresReportTable
            showHeader={false}
            filters={{
              startDate: startDateWithTime || '',
              endDate: endDateWithTime || '',
              storeIds,
            }}
          />
          <DaiDivider />
        </>
      )}
    </>
  )
}

export default HomeSummary
